/**
 * Dashboard tab toolbar (prototype `.dash-bar`): switcher on the left;
 * "Build with AI" (S9 slide-over), "+ Tile" (S6 add-tile dialog), edit toggle
 * and the dashboard kebab (Duplicate / Settings / Delete) on the right.
 * Time-range segment + parameter chips slot in with S10.
 */
import { useState, type ReactNode } from 'react';
import { Check, Info, MoreVertical, Pencil, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useHasProAccess } from '@/core/licensing';
import { FREE_DASHBOARD_LIMIT, type Dashboard } from '../types/dashboardTypes';
import { DashboardSwitcher } from './DashboardSwitcher';
import { BulkRefreshOverride } from './BulkRefreshOverride';

type DashboardToolbarProps = {
  dashboard: Dashboard;
  dashboards: Dashboard[];
  editMode: boolean;
  onToggleEdit: () => void;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onAddTile: () => void;
  onOpenAiPanel: () => void;
  onDuplicate: () => void;
  onOpenSettings: () => void;
  onDelete: () => void;
  /** Manual "Refresh all" — re-runs every query tile now (respects filters). */
  onRefreshAll: () => void;
  /** No query tiles to refresh — the button is disabled with a reason. */
  refreshAllDisabled?: boolean;
  /** Bulk-refresh override (S12) — see `BulkRefreshOverride`. */
  onBulkRefreshOverride: (seconds: number | null) => void;
  /** Chrome-less fullscreen presentation (S13) — see `PresentMode`. */
  onPresent: () => void;
  /** Extra kebab entries (S14 export/import) rendered before the delete group. */
  extraKebabItems?: ReactNode;
};

export function DashboardToolbar({
  dashboard,
  dashboards,
  editMode,
  onToggleEdit,
  onSelect,
  onCreate,
  onAddTile,
  onOpenAiPanel,
  onDuplicate,
  onOpenSettings,
  onDelete,
  onRefreshAll,
  refreshAllDisabled,
  onBulkRefreshOverride,
  onPresent,
  extraKebabItems,
}: DashboardToolbarProps) {
  // Controlled + closed on any item click (GridExportCopyMenu pattern):
  // Radix's close-on-select is unreliable when item handlers kick off async
  // IPC work — an uncontrolled menu can linger open as a zombie overlay.
  const [kebabOpen, setKebabOpen] = useState(false);
  // S16: preemptively disable Duplicate at the free cap (single atomic
  // action, no reason to stay reachable past it) — matches the Object
  // Explorer node's context menu (dashboardExplorerContextMenu.ts).
  const hasProAccess = useHasProAccess();
  const duplicateBlocked = !hasProAccess && dashboards.length >= FREE_DASHBOARD_LIMIT;
  const duplicateWhy = duplicateBlocked
    ? `Free plan is limited to ${FREE_DASHBOARD_LIMIT} dashboards — upgrade to Pro for unlimited.`
    : undefined;
  return (
    <div
      className="flex items-center gap-2 border-b border-border bg-background px-4 py-2"
      data-testid="dashboard-toolbar"
    >
      <DashboardSwitcher
        dashboards={dashboards}
        activeId={dashboard.id}
        onSelect={onSelect}
        onCreate={onCreate}
      />
      {/* Read-only echo of the dashboard's description (edited in Settings) —
          rendered only when there is one, so a dashboard without one keeps the
          toolbar exactly as it was. Passive display: no analytics. */}
      {dashboard.description?.trim() && (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Dashboard description: ${dashboard.description.trim()}`}
              data-testid="dashboard-description-info"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-80 whitespace-pre-line">
            {dashboard.description.trim()}
          </TooltipContent>
        </Tooltip>
      )}
      <span className="flex-1" />
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5"
        onClick={onRefreshAll}
        disabled={refreshAllDisabled}
        title={
          refreshAllDisabled
            ? 'No query tiles to refresh'
            : 'Refresh all tiles now (keeps active filters)'
        }
        data-testid="dashboard-refresh-all-button"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Refresh
      </Button>
      <BulkRefreshOverride dashboard={dashboard} onApply={onBulkRefreshOverride} />
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 text-violet-600 dark:text-violet-400"
        onClick={onOpenAiPanel}
        title="Propose tiles from a prompt — read-only until you add them"
        data-testid="dashboard-ai-button"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Build with AI
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5"
        onClick={onAddTile}
        title="Add a tile — preview a query and pick a visualization"
        data-testid="dashboard-add-tile-button"
      >
        <Plus className="h-3.5 w-3.5" />
        Tile
      </Button>
      <Button
        variant={editMode ? 'default' : 'outline'}
        size="sm"
        className="h-7 gap-1.5"
        onClick={onToggleEdit}
        title={editMode ? 'Finish editing the layout' : 'Edit layout — drag, resize, remove tiles'}
        data-testid="dashboard-edit-button"
      >
        {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
        {editMode ? 'Done' : 'Edit'}
      </Button>
      <DropdownMenu open={kebabOpen} onOpenChange={setKebabOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Dashboard options"
            aria-label="Dashboard options"
            data-testid="dashboard-kebab-button"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        {/* onClickCapture: close BEFORE any item handler can swallow the
            event — bubble-phase close proved flaky for async-IPC items. */}
        <DropdownMenuContent align="end" onClickCapture={() => setKebabOpen(false)}>
          <DropdownMenuItem onClick={onPresent} data-testid="dashboard-present-item">
            Present…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <span title={duplicateWhy}>
            <DropdownMenuItem
              disabled={duplicateBlocked}
              onClick={onDuplicate}
              data-testid="dashboard-duplicate-item"
            >
              Duplicate dashboard
            </DropdownMenuItem>
          </span>
          <DropdownMenuItem onClick={onOpenSettings} data-testid="dashboard-settings-item">
            Settings…
          </DropdownMenuItem>
          {extraKebabItems}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDelete}
            className="text-destructive focus:text-destructive"
            data-testid="dashboard-delete-item"
          >
            Delete dashboard…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
